import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { CanAccess, useInvalidate, useNavigation } from '@refinedev/core';

import {
  Button,
  Chip,
  Loading,
  useSnackbar,
} from '@rrexpress/core';
import { ColumnDef } from '@tanstack/react-table';
import { isEmpty } from 'lodash';

import ActionDelete from '@/components/action-button/delete';
import ActionUpdate from '@/components/action-button/update';
import BaseList from '@/components/base-list';
import useCanPerform from '@/hooks/useCanPerform';
import useLayout from '@/hooks/useLayout';
import { useModal } from '@/hooks/useModal';
import useBranch from '@/modules/branch/application/branch';
import { type IBranch } from '@/modules/branch/model/branch';

const Branch = () => {
  useLayout({ title: 'Cabang' });
  const { edit, create } = useNavigation();
  const { open, close } = useModal();

  const { deleteBranch, loadingDelete } = useBranch();
  const { show } = useSnackbar();
  const invalidate = useInvalidate();

  const canPerform = useCanPerform('branches');

  async function handleOnDelete(id: string, rowVersion: string) {
    try {
      await deleteBranch(id, rowVersion);
      close();
      show('Berhasil menghapus cabang', 'success');
      invalidate({ resource: 'branches', invalidates: ['list'] });
    } catch (error) {
      show('Gagal menghapus cabang', 'error');
    }
  }

  const columns: ColumnDef<IBranch>[] = useMemo(() => [
    {
      id: 'code',
      accessorKey: 'branch_code',
      header: 'Kode Cabang',
      cell: ({ row }) => (
        <Link to={`/branches/show/${row.original.branch_id}`}>
          <span className="text-blue-500">{row.original.branch_code}</span>
        </Link>
      ),
    },
    {
      id: 'name',
      accessorKey: 'branch_name',
      header: 'Nama Cabang',
      cell: ({ row }) => (
        <Link to={`/branches/show/${row.original.branch_id}`}>
          <span className="text-blue-500">{row.original.branch_name}</span>
        </Link>
      ),
    },
    {
      id: 'city',
      accessorKey: 'address_city_name',
      header: 'Kota',
    },
    {
      id: 'is_active',
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ getValue }) => <Chip
        label={getValue() ? 'Aktif' : 'Tidak Aktif'}
        backgroundColor={getValue() ? '#E6F7F4' : '#DFE0E9'}
        borderColor={getValue() ? '#E6F7F4' : '#DFE0E9'}
        color={getValue() ? '#006C5A' : '#888888'}
      />,
    },
    {
      id: 'action',
      accessorKey: 'branch_id',
      header: 'Action',
      cell: ({ getValue, row }) => (
        <div className="flex flex-row gap-x-2">
          <CanAccess resource="branches" action="edit">
            <ActionUpdate onClick={() => edit('branches', getValue() as string)} />
          </CanAccess>
          <CanAccess resource="branches" action="delete">
            <ActionDelete
              onClick={() => open({
                title: 'Hapus Cabang',
                template: loadingDelete
                  ? (
                    <Loading
                      variant="circular"
                      size={40}
                      speed={0.6}
                      indicatorCap="round"
                    />
                  )
                  : (
                    <p className="text-[#222222]">
                      Apakah anda yakin akan menghapus <span className="font-medium">{row?.original?.branch_name}</span> dari daftar cabang?
                    </p>
                  ),
                footer: (
                  <div className="flex gap-3">
                    <Button variant="text" onClick={() => close()}>Batal</Button>
                    <Button backgroundColor="#BE321E" onClick={() => handleOnDelete(getValue() as string, row.original.row_version)}>
                      Hapus
                    </Button>
                  </div>
                ),
              })}
            />
          </CanAccess>
        </div>
      ),
    },
  ], []);

  return (
    <BaseList
      resource="branches"
      title="Daftar Cabang"
      columns={columns as any}
      filter={{
        sections: [
          {
            layout: {
              direction: 'horizontal',
              sections: [
                {
                  input: {
                    name: 'type',
                    type: 'dropdown',
                    label: 'Type',
                    comboboxOptions: [
                      { label: 'Nama Cabang', value: 'name' },
                      { label: 'Kode Cabang', value: 'code' },
                    ],
                  },
                },
                {
                  input: {
                    name: 'keyword',
                    type: 'textfield',
                    label: 'Keyword',
                  },
                },
                {
                  input: {
                    type: 'dropdown',
                    name: 'is_active',
                    label: 'Aktif',
                    defaultValue: 'all',
                    comboboxOptions: [
                      {
                        label: 'Semua',
                        value: 'all',
                      },
                      {
                        label: 'Aktif',
                        value: 'true',
                      },
                      {
                        label: 'Tidak Aktif',
                        value: 'false',
                      },
                    ],
                  },
                },
                {
                  input: {
                    type: 'queryfield',
                    name: 'address_city_id',
                    label: 'Kota',
                    comboboxResource: 'cities',
                    queryfieldKeyOptionLabel: 'city_name',
                    queryfieldKeyOptionValue: 'city_id',
                    queryfieldKeyQueryFilter: 'city_name',
                    dataProviderName: 'masterdata',
                  },
                },
              ],
            },
          },
        ],
        onSubmit: (rawFilterData) => {
          if (!isEmpty(rawFilterData)) {
            const newFilter = { ...rawFilterData };

            if (newFilter?.is_active === 'true') newFilter.is_active = true;
            if (newFilter?.is_active === 'false') newFilter.is_active = false;
            if (newFilter?.is_active === 'all') newFilter.is_active = undefined;

            return {
              ...newFilter,
              branch_name: newFilter.type === 'name' && newFilter.keyword ? newFilter.keyword : null,
              branch_code: newFilter.type === 'code' && newFilter.keyword ? newFilter.keyword : null,
            };
          }
          return rawFilterData;
        },
      }}
      headerActions={[
        {
          type: 'tertiary',
          label: 'Tambah Baru',
          onClick: () => {
            if (canPerform.create) create('branches');
          },
        },
      ]}
    />
  );
};

export default Branch;
